import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../api/axiosClient';
import { CategoryRowType } from '../types/categories';

interface SimilarColorResponse {
  id: number;
  main_color_id: number;
  similar_color_id: number;
}

interface CategoriesContextType {
  recyclingCat: CategoryRowType[];
  setRecyclingCat: React.Dispatch<React.SetStateAction<CategoryRowType[]>>;
  reuseCat: CategoryRowType[];
  setReuseCat: React.Dispatch<React.SetStateAction<CategoryRowType[]>>;
  colorCat: CategoryRowType[];
  setColorCat: React.Dispatch<React.SetStateAction<CategoryRowType[]>>;
  similarColors: Record<number, number[]>;
  fetchSimilarColors: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  loading: boolean;
}

interface CategoriesProviderProps {
  children: ReactNode;
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined);

export const CategoriesProvider = ({ children }: CategoriesProviderProps) => {
  const [recyclingCat, setRecyclingCat] = useState<CategoryRowType[]>([]);
  const [reuseCat, setReuseCat] = useState<CategoryRowType[]>([]);
  const [colorCat, setColorCat] = useState<CategoryRowType[]>([]);
  const [similarColors, setSimilarColors] = useState<Record<number, number[]>>({});
  const [loading, setLoading] = useState<boolean>(true);

  const fetchSimilarColors = async () => {
    try {
      const response = await api.get<SimilarColorResponse[]>('/similar-colors');
      const similarMap = response.data.reduce<Record<number, number[]>>((acc, item) => {
        if (!acc[item.main_color_id]) {
          acc[item.main_color_id] = [];
        }
        acc[item.main_color_id].push(item.similar_color_id);
        return acc;
      }, {});
      setSimilarColors(similarMap);
    } catch (error) {
      console.error('Error fetching similar colors:', error);
      setSimilarColors({});
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const [response, response2, response3] = await Promise.all([
        api.get<CategoryRowType[]>(`/categories/getAll`),
        api.get<CategoryRowType[]>(`/sorter/reuse-category`),
        api.get<CategoryRowType[]>(`/colors/getAll`),
      ]);

      console.log('Fetched categories:', response.data);
      setRecyclingCat(
        response.data
          .filter(cat => !cat.deleted)
          .map((cat: any) => {
            const { medusa_enabled, ...rest } = cat;
            return {
              ...rest,
              enable: rest.enable ?? medusa_enabled ?? false,
            };
          })
      );
      setReuseCat(response2.data.filter(cat => !cat.deleted));
      setColorCat(response3.data.filter(cat => !cat.deleted));
    } catch (error) {
      console.error('Error fetching categories:', error);
      setRecyclingCat([]);
      setReuseCat([]);
      setColorCat([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSimilarColors();
  }, []);

  return (
    <CategoriesContext.Provider
      value={{
        recyclingCat,
        setRecyclingCat,
        reuseCat,
        setReuseCat,
        colorCat,
        setColorCat,
        similarColors,
        fetchSimilarColors,
        fetchCategories,
        loading,
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
};

export const useCategories = (): CategoriesContextType => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoriesProvider');
  }
  return context;
};
